import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'

import { AccordionDetails, IconButton } from '@mui/material'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { getErrorByPath } from '../../../app/utils'
import StyledAccordionContainer from '../../../components/Accordions/StyledAccordionContainer'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import BoxAlert from '../../../components/Alerts/BoxAlert'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import { AccordionHeader } from '../../../components/Headers/styles'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useOrderStatus } from '../hooks/useOrderStatus'
import ConsignmentService from './ConsignmentService/ConsignmentService'
import PackagingForm from './PackagingForm'
import ShipFromDeliverToContainer from './ShipFromDeliverToContainer'
import messages from './messages'

interface ConsignmentFormProps {
  index: number
  removeConsignment: (index: number) => void
  copyConsignment: (index: number) => void
}

const ConsignmentForm: React.FC<ConsignmentFormProps> = ({
  index,
  removeConsignment,
  copyConsignment,
}) => {
  const [expanded, setExpanded] = useState(true)
  const { currentPage } = useOrderFormContext()
  const { formatMessage } = useIntl()
  const {
    formState: { errors },
  } = useFormContext()
  const { isNotDrafted } = useOrderStatus()

  const consignmentClearingRequiredError = getErrorByPath(
    errors,
    `bookings.${index}.clearingRequired`
  )

  const hasConsignmentErrors =
    Array.isArray(errors.bookings) &&
    !!errors.bookings[index] &&
    Object.keys(errors.bookings[index]).length > 0

  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const renderShipFromDeliverToContainer = () => {
    return currentPage === 1 ? (
      <>
        <ShipFromDeliverToContainer consignmentIndex={index} />
      </>
    ) : null
  }

  const renderPackagingForm = () => {
    return currentPage === 2 || currentPage === 3 ? (
      <>
        <PackagingForm consignmentIndex={index} />
      </>
    ) : null
  }

  const renderConsignmentServices = () => {
    return currentPage === 4 ? (
      <ConsignmentService expanded={true} consignmentIndex={index} />
    ) : null
  }

  return (
    <>
      <StyledAccordionContainer
        expanded={expanded}
        sx={(theme) => ({
          backgroundColor: theme.palette?.accordion?.parent?.bg,
          border: hasConsignmentErrors ? '1px solid #f44336' : undefined,
          borderRadius: hasConsignmentErrors ? '8px' : undefined,
        })}
        onChange={handleToggleAccordion}
      >
        <StyledAccordionSummary index={`consignment-content-${index}`} expanded={expanded}>
          <AccordionHeader sx={{ flexGrow: 1 }}>
            {formatMessage(messages.consignmentItem, { idx: index + 1 })}
          </AccordionHeader>
          {consignmentClearingRequiredError && (
            <BoxAlert
              severity='error'
              message={
                typeof consignmentClearingRequiredError === 'string'
                  ? consignmentClearingRequiredError
                  : consignmentClearingRequiredError?.message ||
                    (Array.isArray(consignmentClearingRequiredError)
                      ? consignmentClearingRequiredError[0]
                      : '') ||
                    ''
              }
              boxProps={{ xs: 12, sx: { pb: 0, pt: 1 } }}
            />
          )}
          <ConditionalRender condition={currentPage === 1 && !isNotDrafted}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                if (expanded) {
                  setExpanded(false)
                }
                copyConsignment(index)
              }}
              color='primary'
            >
              <CopyIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                removeConsignment(index)
              }}
              color='error'
            >
              <DeleteIcon />
            </IconButton>
          </ConditionalRender>
        </StyledAccordionSummary>
        <AccordionDetails>
          {renderShipFromDeliverToContainer()}
          {renderPackagingForm()}
          {renderConsignmentServices()}
        </AccordionDetails>
      </StyledAccordionContainer>
    </>
  )
}

export default ConsignmentForm
